import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Pagination,
  Paper,
  Popper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import store from "../../../../../store";
import { getWorkspaceImage } from "../../../../../core/layouts/shared/workspaceImages";
import { colors, spacing, typography } from "../../../../../assets/styles/tokens";
import { selectUserToken, setDisplayErrorSnackBar } from "../../../../../features/userSlice";
import { setModalOpen } from "../../../../../features/brandConfigurationReportSlice";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import { useCopyToClipboard } from "../brandPropertiesHook/useCopyToClipboard";
import RunBrandData from "../BrandConfigurationOverview/RunModals/RunBrandData/RunBrandData";
import BrandsStatusBadge from "../BrandsStatusBadge";
import BrandsQuickActions from "../BrandsQuickActions";
import IndustryBadge from "../IndustryBadge";
import { ensureRunProcessSupportData } from "../utils/ensureRunProcessSupportData";
import { useAutomaticConfigure } from "../utils/useAutomaticConfigure";
import { useBrands } from "../utils/useBrands";
import {
  ACCOUNT_NAME_MAX_CHARS,
  WEBSITE_NAME_MAX_CHARS,
  truncateWithEllipsis,
} from "../utils/helpersOfBrands";

interface Props {
  rows: any[];
  selectedRows: number[];
  setSelectedRows: (rows: number[]) => void;
}

interface TableHeader {
  id: number;
  key: string;
  label: string;
  sortable: boolean;
  // Floor for a column's width. Status carries one so the column never
  // collapses narrower than its widest badge ("Account Issue" + its
  // sub-label), which is 160px in Figma.
  minWidth?: number;
}

const tableHeaders: TableHeader[] = [
  { id: 0, key: "brand_name", label: "Brand Name", sortable: true },
  { id: 1, key: "brand_id", label: "Brand ID", sortable: true },
  { id: 2, key: "account_username", label: "Account", sortable: true },
  { id: 3, key: "company_name", label: "Company", sortable: true },
  { id: 4, key: "affiliate_system", label: "Affiliate System", sortable: true },
  { id: 5, key: "website_name", label: "Website", sortable: true },
  { id: 6, key: "status", label: "Status", sortable: true, minWidth: 160 },
  { id: 7, key: "quick_actions", label: "Quick Actions", sortable: false },
];

const CopyKeeperIdIcon = ({
  keeperId,
  copied,
  onCopy,
}: {
  keeperId?: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <IconButton
    size="small"
    disabled={!keeperId}
    color={copied ? "success" : "secondary"}
    onClick={(e) => {
      e.stopPropagation();
      onCopy();
    }}
  >
    {copied ? (
      <CheckCircleOutlinedIcon fontSize="inherit" />
    ) : (
      <ContentCopyIcon fontSize="inherit" />
    )}
  </IconButton>
);

// Opens the brand's own login_url (from the API) in a new tab. Always
// visible: disabled/grey when there's no login_url, secondary-colored and
// clickable when there is one.
const OpenLoginUrlIcon = ({ loginUrl }: { loginUrl?: string }) => (
  <Tooltip title={loginUrl ? "Open login URL" : ""}>
    <span>
      {loginUrl ? (
        <RouterLink to={loginUrl} target="_blank">
          <IconButton size="small" color="secondary">
            <OpenInNewIcon fontSize="inherit" />
          </IconButton>
        </RouterLink>
      ) : (
        <IconButton size="small" disabled>
          <OpenInNewIcon fontSize="inherit" />
        </IconButton>
      )}
    </span>
  </Tooltip>
);

// Matches the site icon/image styling used by the workspace switcher dropdown
// in the drawer (WorkspaceDropdown.tsx) — same image lookup, same square Avatar.
const WebsiteIcon = ({ websiteName }: { websiteName?: string }) => {
  const image = getWorkspaceImage(websiteName);
  return (
    <Avatar
      src={image || undefined}
      sx={{
        bgcolor: image ? "transparent" : colors.Brand[700].$value,
        color: image ? "transparent" : colors.Base.White.$value,
        width: spacing.Spacing["4xl"].$value,
        height: spacing.Spacing["4xl"].$value,
        borderRadius: spacing.Radius.xs.$value,
        fontSize: typography.Size.Text.sm.$value,
        fontWeight: typography.Font_weight.Medium.$value,
      }}
    >
      {!image && websiteName?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
};

const AffiliateSystemCell = ({
  affiliateSystem,
  loginUrl,
  keeperId,
}: {
  affiliateSystem?: string;
  loginUrl?: string;
  keeperId?: string;
}) => {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {loginUrl ? (
          <Typography
            component={RouterLink}
            to={loginUrl}
            target="_blank"
            fontSize="14px"
            sx={{ color: "secondary.main", textDecoration: "none" }}
          >
            {affiliateSystem ?? "N/A"}
          </Typography>
        ) : (
          <Typography fontSize="14px">{affiliateSystem ?? "N/A"}</Typography>
        )}
        <OpenLoginUrlIcon loginUrl={loginUrl} />
      </Stack>
      {/* Single Tooltip over the whole icon+text unit — same pattern as the
          Copy Keeper ID control on the Brand Overview page — so hovering
          either the icon or the text shows "Copy Keeper ID". */}
      <Tooltip title={!keeperId ? "" : copied ? "Copied!" : "Copy Keeper ID"}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          onClick={() => keeperId && copy(keeperId)}
          sx={{ width: "fit-content", cursor: keeperId ? "pointer" : "default" }}
        >
          <CopyKeeperIdIcon
            keeperId={keeperId}
            copied={copied}
            onCopy={() => keeperId && copy(keeperId)}
          />
          <Typography
            fontSize="11px"
            sx={{
              textDecoration: "underline",
              color: keeperId ? "secondary.main" : "text.disabled",
            }}
          >
            Keeper ID
          </Typography>
        </Stack>
      </Tooltip>
    </Stack>
  );
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

// Custom "Rows per page" control replacing MUI's Select-based one. MUI's
// Select/Menu is built on Popover -> Modal, which always renders a
// full-viewport backdrop to catch outside clicks — making that backdrop
// pointer-events-transparent (to let checkbox clicks through) also breaks
// its own click-to-close behavior, since it needs to receive the click to
// close. Popper has no backdrop at all, so a click on a checkbox behind it
// reaches the checkbox completely normally, while ClickAwayListener (a
// plain event listener, not an overlay) still detects the outside click and
// closes the dropdown without consuming it.
const RowsPerPageControl = ({
  value,
  onChange,
  count,
  page,
}: {
  value: number;
  onChange: (value: number) => void;
  count: number;
  page: number;
}) => {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);

  const from = count === 0 ? 0 : page * value + 1;
  const to = Math.min(count, (page + 1) * value);

  return (
    <Stack direction="row" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>
        <Box
          ref={anchorRef}
          onClick={() => setOpen((prev) => !prev)}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
        >
          <Typography variant="body2">{value}</Typography>
          <ArrowDropDownIcon fontSize="small" />
        </Box>
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper elevation={3}>
              <MenuList dense>
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option}
                    selected={option === value}
                    onClick={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        {from}&ndash;{to} of {count}
      </Typography>
    </Stack>
  );
};

// All Brands table: checkbox selection, sortable/paginated columns per spec,
// and the full quick-action set per row. Mounts the Run Process wizard and
// Reconfigure warning popup once, exactly like BrandsTable.tsx (legacy
// brand_data module) and BrandConfigurationReportTableData.tsx do.
const AllBrandsTable = ({ rows, selectedRows, setSelectedRows }: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const { isModalOpen } = useSelector((state: any) => state.brandConfigurationReport);
  const { fetchedBrandsData, beginLoading, endLoading } = useBrands();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sortKey, setSortKey] = React.useState<string | null>(
    () => searchParams.get("sort"),
  );
  const [sortDir, setSortDir] = React.useState<"asc" | "desc" | null>(
    () => (searchParams.get("dir") as "asc" | "desc" | null) ?? null,
  );
  const [page, setPage] = React.useState<number>(() => {
    const fromUrl = Number(searchParams.get("page"));
    return Number.isInteger(fromUrl) && fromUrl >= 0 ? fromUrl : 0;
  });
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { openAutomaticConfigure, automaticConfigureDialogs } = useAutomaticConfigure();

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (sortKey) next.set("sort", sortKey);
        else next.delete("sort");
        if (sortDir) next.set("dir", sortDir);
        else next.delete("dir");
        if (page) next.set("page", String(page));
        else next.delete("page");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDir, page]);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;
    if (sortKey === header.key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(header.key);
      setSortDir("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    return [...rows].sort((a, b) => {
      const aValue = a?.[sortKey];
      const bValue = b?.[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDir === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortDir === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rows, sortKey, sortDir]);

  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    // Selections are page-local — moving to another page clears whatever
    // was checked on the previous one, including the header checkbox.
    setSelectedRows([]);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Rows per page changed the set of rows a "select all" would apply to —
    // clear any existing selection (including the header checkbox) instead
    // of leaving a stale/misleading selection behind.
    setSelectedRows([]);
  };

  const handleCheckboxChange = (brandId: number) => {
    const next = new Set(selectedRows);
    if (next.has(brandId)) next.delete(brandId);
    else next.add(brandId);
    setSelectedRows(Array.from(next));
  };

  const paginatedRowIds = paginatedRows.map((row: any) => row.brand_id);
  const allPageRowsSelected =
    paginatedRowIds.length > 0 &&
    paginatedRowIds.every((id: number) => selectedRows.includes(id));
  const somePageRowsSelected = paginatedRowIds.some((id: number) =>
    selectedRows.includes(id),
  );

  const handleHeaderCheckboxChange = () => {
    if (allPageRowsSelected) {
      setSelectedRows(
        selectedRows.filter((id) => !paginatedRowIds.includes(id)),
      );
    } else {
      setSelectedRows(Array.from(new Set([...selectedRows, ...paginatedRowIds])));
    }
  };

  const handleRunBrand = (row: any) => {
    ensureRunProcessSupportData(oauthTokenHeader);
    dispatch(
      setModalOpen({
        isOpen: true,
        data: [
          {
            brandId: row.brand_id,
            brandName: row.brand_name,
            // Not available in the Brands query — see constants.ts note.
            row_output_table_data: [],
          },
        ],
      }),
    );
  };

  const handleTryToConnect = async (row: any) => {
    // Show the same whole-tab BrandsSkeleton used on first load for the
    // entire operation (the connect request itself, not just the refetch
    // after it succeeds).
    beginLoading();
    let didRefetch = false;
    try {
      const response = await BrandsEndPointLogic.tryToConnect(oauthTokenHeader, [
        row.brand_id,
      ]);
      if (response?.status === 200) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: `${row.brand_name} connect request sent.`,
            type: "success",
          }),
        );
        // Refresh so the brand's updated status/tab membership shows up —
        // fetchedBrandsData() ends the loading state itself once done.
        await fetchedBrandsData();
        didRefetch = true;
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: `Failed to connect ${row.brand_name}.`,
            type: "error",
          }),
        );
      }
    } catch {
      // BrandsEndPointLogic.tryToConnect already dispatches the error snackbar.
    } finally {
      if (!didRefetch) endLoading();
    }
  };

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                color="secondary"
                checked={allPageRowsSelected}
                indeterminate={!allPageRowsSelected && somePageRowsSelected}
                onChange={handleHeaderCheckboxChange}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell key={header.id} sx={{ minWidth: header.minWidth }}>
                <TableSortLabel
                  active={sortKey === header.key}
                  direction={sortKey === header.key ? sortDir ?? "asc" : "asc"}
                  onClick={() => handleHeaderClick(header)}
                  disabled={!header.sortable}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={tableHeaders.length + 1}
                align="center"
                sx={{ padding: "20px", color: "#888", fontSize: "16px" }}
              >
                No Data To Display
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row: any) => (
              <TableRow key={row.brand_id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={selectedRows.includes(row.brand_id)}
                    onChange={() => handleCheckboxChange(row.brand_id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography fontSize="14px">{row.brand_name}</Typography>
                  <IndustryBadge industryName={row.industry_name} />
                </TableCell>
                <TableCell>{row.brand_id ?? "N/A"}</TableCell>
                <TableCell>
                  {row.account_username ? (
                    <Tooltip title={row.account_username} placement="top" arrow>
                      <span>
                        {truncateWithEllipsis(
                          row.account_username,
                          ACCOUNT_NAME_MAX_CHARS,
                        )}
                      </span>
                    </Tooltip>
                  ) : (
                    "N/A"
                  )}
                </TableCell>
                <TableCell>{row.company_name ?? "N/A"}</TableCell>
                <TableCell>
                  <AffiliateSystemCell
                    affiliateSystem={row.affiliate_system}
                    loginUrl={row.login_url}
                    keeperId={row.keeper_id}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WebsiteIcon websiteName={row.website_name} />
                    <Box>
                      <Typography fontSize="14px">
                        {row.website_name
                          ? truncateWithEllipsis(
                              row.website_name,
                              WEBSITE_NAME_MAX_CHARS,
                            )
                          : "N/A"}
                      </Typography>
                      {row.website_alias && (
                        <Typography fontSize="12px" color="text.secondary">
                          {row.website_alias}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <BrandsStatusBadge
                    status={row.status}
                    brandIsActive={row.brand_is_active}
                    accountStatusName={row.account_status_name}
                  />
                </TableCell>
                <TableCell>
                  <BrandsQuickActions
                    row={row}
                    onRunBrand={handleRunBrand}
                    onReconfigure={openAutomaticConfigure}
                    onTryToConnect={handleTryToConnect}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
        }}
      >
        <Box sx={{ justifySelf: "start" }}>
          <RowsPerPageControl
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            count={sortedRows.length}
            page={page}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))}
          page={page + 1}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
          sx={{ justifySelf: "center" }}
        />
        <Box />
      </Box>

      <RunBrandData open={isModalOpen} />
      {automaticConfigureDialogs}
    </TableContainer>
  );
};

export default AllBrandsTable;
